import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import {
	DEFAULT_TIMEOUT_USER_EXP,
	ExperienceSuccess,
	useExpTrackerStart,
	useExpTrackerStop,
	UserExperienceNames,
	UserStatus,
} from '@atlassian/ptc-common';

import EmptyPlacesListState from '../EmptyPlacesListState';
import { PanelContent, PanelContentHeader, PanelContentTitle } from '../PanelContent';
import ProfilePlacesItem from '../ProfilePlacesItem';

import * as Styled from './ProfilePlaces.styled';
import { type ProfilePlacesProps } from './types';

const MAX_PLACES_LIMIT = 50;

function ProfilePlaces(props: ProfilePlacesProps) {
	const {
		hasError,
		error,
		hasLoaded,
		isCurrentUser,
		userStatus,
		items = [],
		userId,
		userFullName,
	} = props;

	useExpTrackerStart({
		id: userId,
		name: UserExperienceNames.USER_PROFILE_CONTAINERS,
		timeout: DEFAULT_TIMEOUT_USER_EXP,
	});

	useExpTrackerStop({
		name: UserExperienceNames.USER_PROFILE_CONTAINERS,
		error,
	});

	const renderSkeleton = () => {
		const defaultNumOfPlaces = 4;
		return Array(defaultNumOfPlaces)
			.fill(undefined)
			.map((_, index) => (
				<ProfilePlacesItem key={`item-${index}`} type="space" name="" path="" hasLoaded={false} />
			));
	};

	const renderPlacesList = () => {
		/**
		 * NOTE: Following is just a hack to limit the total number of boards.
		 * In real life the total number of places will be far less than this.
		 * But since there's no guarantee that the backend will not be sending
		 * a very large number of boards, the following limit is enforced.
		 */
		const places = items.slice(0, MAX_PLACES_LIMIT);

		return (
			<Styled.ProfilePlacesList data-test-selector="profile-places">
				{hasLoaded
					? places.map((item) => (
							<ProfilePlacesItem
								key={`${item.type}-${item.name}`}
								type={item.type}
								name={item.name}
								path={item.path}
								icon={item.icon}
								hasLoaded
							/>
						))
					: renderSkeleton()}
			</Styled.ProfilePlacesList>
		);
	};

	const headline = isCurrentUser
		? messages.ownerHeadline
		: userStatus === UserStatus.inactive || userStatus === UserStatus.closed
			? messages.otherHeadlinePast
			: messages.otherHeadline;

	// loading --> rendering skeleton
	// not loading + empty data --> rendering empty state and ExperienceSuccess
	// not loading + has error --> rendering nothing and "useExpTrackerStop" will trigger the stop experience event.
	// not loading + has data --> render UI and ExperienceSuccess

	if (hasLoaded && !hasError && items.length === 0) {
		// trigger success event when data is loaded but it's empty
		return (
			<PanelContent>
				<PanelContentHeader>
					<PanelContentTitle>
						<FormattedMessage {...headline} />
					</PanelContentTitle>
				</PanelContentHeader>
				<Styled.EmptyPlacesList>
					<EmptyPlacesListState
						isCurrentUser={isCurrentUser || false}
						userFullName={userFullName}
						userStatus={userStatus}
					/>
				</Styled.EmptyPlacesList>
				<ExperienceSuccess hasLoaded name={UserExperienceNames.USER_PROFILE_CONTAINERS} />
			</PanelContent>
		);
	}

	if (hasError) {
		return null;
	}

	return (
		<PanelContent>
			<PanelContentHeader>
				<PanelContentTitle>
					<FormattedMessage {...headline} />
				</PanelContentTitle>
			</PanelContentHeader>
			{renderPlacesList()}
			<ExperienceSuccess
				hasLoaded={hasLoaded && !hasError}
				name={UserExperienceNames.USER_PROFILE_CONTAINERS}
			/>
		</PanelContent>
	);
}

export const messages = defineMessages({
	ownerHeadline: {
		id: 'pf-home.profile-page.places-list.owner.headline',
		defaultMessage: 'Places you work in',
		description:
			'Section headline for Places where a users personal Confluence space and Jira boards shown to profile owner',
	},
	otherHeadline: {
		id: 'pf-home.profile-page.places-list.other.headline',
		defaultMessage: 'Places they work in',
		description:
			'Section headline for Places where a users personal Confluence space and Jira boards shown to a profile visitor',
	},
	otherHeadlinePast: {
		id: 'pf-home.profile-page.places-list.other.headline-past',
		defaultMessage: 'Places they work in',
		description:
			'Section headline for Places where a deactivated or deleted users personal Confluence space and Jira boards shown to a profile visitor',
	},
});

export default ProfilePlaces;
